import { faCheck, faCircleNotch, faShieldAlt, faTimes } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Checkbox from "@/components/checkbox"
import Chip from "@/components/chip"
import ListCell from "@/components/list-cell"
import ListContainer from "@/components/list-container"
import ListIcon from "@/components/list-icon"
import ListItem from "@/components/list-item"
import ListFields from "@/components/list-fields"
import ProgressBar from "@/components/progress-bar"
import Space from "@/components/space"
import { store } from "@/index"
import { chunk, flatten, sortBy, uniq } from "lodash"
import { useEffect, useState } from "react"
import { WithTranslation, withTranslation } from "react-i18next"
import { Account } from "@/redux/account.types"
import { sendingFetchOpenSurveys_AsSupervisor, sendingFetch_AsSupervisor } from "@/redux/sending.actions"
import { surveyFetch_AsSupervisor } from "@/redux/survey.actions"
import { formatDate } from "@/utils/format-date.utils"
import { getImageUrl } from "@/utils/get-image-url"

interface OwnProps {
  onClick: Function
}

type Props = OwnProps & WithTranslation

//Open surveys without sending : the ones open for longer than this are flagged (candidates to be closed)
const OPEN_SURVEYS_OLD_DAYS: number = 365

//Surveys per participation request : the backend (qs) only parses arrays of up to 20 items in a query string,
//above that surveyIds becomes an object and the request returns nothing
const PARTICIPATION_CHUNK_SIZE: number = 20

//Views of the tab : planned sendings (list of sendings) or open surveys without sending (list of surveys)
const VIEW_SENDINGS: string = "VIEW_SENDINGS"
const VIEW_OPEN_SURVEYS: string = "VIEW_OPEN_SURVEYS"

function SendingsAllWidget(props: Props) {
  const { t } = props

  const [sendings, setSendings] = useState<any[]>([])

  useEffect(() => {
    async function loadSendings() {
      const sendingResponse: any = await store.dispatch(sendingFetch_AsSupervisor())

      if (!sendingResponse.error && sendingResponse.length > 0) {
        const surveyIds: string[] = uniq(
          flatten(sendingResponse.map((x: any) => x.Project.Surveys.map((y: any) => y.id)))
        )
        const surveyResponse: any = await store.dispatch(surveyFetch_AsSupervisor(surveyIds))

        if (!surveyResponse.error) {
          sendingResponse.forEach((x: any, i: number) => {
            const survey: any = surveyResponse.find((y: any) => x.Project.id === y.project_id)

            if (survey) {
              sendingResponse[i]["Survey"] = survey
            }
          })

          setSendings(sendingResponse)
        }
      }
    }

    loadSendings()
  }, [])

  //Open surveys without any planned sending (ex : QR code only)
  const [view, setView] = useState<string>(VIEW_SENDINGS)
  const [openSurveys, setOpenSurveys] = useState<any[]>([])
  const [openParticipation, setOpenParticipation] = useState<any>({})
  const [search, setSearch] = useState<string>("")
  const [onlyQrcode, setOnlyQrcode] = useState<boolean>(false)
  const [onlyOld, setOnlyOld] = useState<boolean>(false)
  const [participationAttempt, setParticipationAttempt] = useState<number>(0)

  useEffect(() => {
    async function loadOpenSurveys() {
      const response: any = await store.dispatch(sendingFetchOpenSurveys_AsSupervisor())

      if (!response.error) {
        const rows: any[] = flatten(
          response.map((project: any) =>
            (project.Surveys || []).map((survey: any) => ({
              surveyId: survey.id,
              dateStart: survey.dateStart,
              project,
              account: project.Account
            }))
          )
        )
        setOpenSurveys(sortBy(rows, (x: any) => -new Date(x.dateStart).getTime()))
      }
    }

    loadOpenSurveys()
  }, [])

  function getOpenDays(x: any): number {
    return Math.floor((Date.now() - new Date(x.dateStart).getTime()) / (24 * 60 * 60 * 1000))
  }

  function getOpenSince(x: any): string {
    const days: number = getOpenDays(x)
    if (days < 1) return t("sendings_open_age_today")
    if (days < 60) return t("sendings_open_age_days", { count: days })
    if (days < 730) return t("sendings_open_age_months", { count: Math.floor(days / 30.4) })
    return t("sendings_open_age_years", { count: Math.floor(days / 365) })
  }

  function normalize(text: string): string {
    return (text || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
  }

  const searchText: string = normalize(search.trim())
  const displayedOpenSurveys: any[] = openSurveys.filter(
    (x: any) =>
      (!onlyQrcode || x.project.diffusionMode?.qrcode) &&
      (!onlyOld || getOpenDays(x) >= OPEN_SURVEYS_OLD_DAYS) &&
      (!searchText || normalize(x.account.name).includes(searchText) || normalize(x.project.name).includes(searchText))
  )
  const displayedOpenIds: string = displayedOpenSurveys.map((x: any) => x.surveyId).join(",")

  //Surveys requested but without any answer (null) : the request failed or the survey has no archive row
  const failedParticipationCount: number = displayedOpenSurveys.filter(
    (x: any) => openParticipation[x.surveyId] === null
  ).length

  function retryParticipation() {
    setOpenParticipation((current: any) => {
      const next: any = { ...current }
      Object.keys(next).forEach((id: string) => {
        if (next[id] === null) delete next[id]
      })
      return next
    })
    setParticipationAttempt((attempt: number) => attempt + 1)
  }

  //Load participation only for the surveys displayed, once the view is opened
  useEffect(() => {
    if (view !== VIEW_OPEN_SURVEYS) return

    const missingIds: string[] = displayedOpenSurveys.map((x: any) => x.surveyId).filter((id: string) => openParticipation[id] === undefined)
    if (!missingIds.length) return

    async function loadParticipation() {
      const responses: any[] = await Promise.all(
        chunk(missingIds, PARTICIPATION_CHUNK_SIZE).map((ids: string[]) => store.dispatch(surveyFetch_AsSupervisor(ids)))
      )

      setOpenParticipation((current: any) => {
        const next: any = { ...current }
        responses.forEach((response: any) => {
          if (!response.error) response.forEach((x: any) => (next[x.id] = x))
        })
        //Surveys requested but not returned (error, no archive row) : unavailable, not loading anymore
        missingIds.forEach((id: string) => {
          if (next[id] === undefined) next[id] = null
        })
        return next
      })
    }

    loadParticipation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, displayedOpenIds, participationAttempt])

  //Participation of a survey : percentage of answers (bold, fixed width) and progress bar on the same line
  function getParticipation(survey: any) {
    return (
      <div className="flex items-center gap-2" style={{ width: 180 }}>
        <b style={{ width: 40 }}>{`${survey.nb_created ? ((survey.nb_done / survey.nb_created) * 100).toFixed(0) : 0}%`}</b>

        {/* Without the vertical margin of the bar : aligned with the text of the other cells */}
        <div className="flex1 [&_.loading-bar-container]:my-0">
          <ProgressBar
            items={[
              { value: survey.nb_done, text: t("survey_done") },
              { value: survey.nb_started, text: t("survey_started") },
              { value: survey.nb_clicked, text: t("survey_clicked") }
            ]}
            max={survey.nb_created}
          />
        </div>
      </div>
    )
  }

  //Background of a sending : green if sent, red if the date is past and not sent (error), default if upcoming
  //Classes and not bgColor of ListItem : keep the line between the rows and a darker color on hover
  function getSendingClassName(sending: any): string {
    if (sending.isDone) return "!bg-[#e8f7ee] hover:!bg-[#d7f0e1]"
    if (new Date(sending.date).getTime() < Date.now()) return "!bg-[#fdecea] hover:!bg-[#fad9d5]"
    return ""
  }

  return (
    <>
      {/* Same padding as the lists headers (20px 30px), minus the margin of the chips */}
      <div className="flex px-[26px] pt-4">
        <Chip
          isWithMargin
          color={view === VIEW_SENDINGS ? "#1b759a" : null}
          onClick={() => setView(VIEW_SENDINGS)}>
          {t("sendings_view_planned")}
        </Chip>
        <Chip
          isWithMargin
          color={view === VIEW_OPEN_SURVEYS ? "#1b759a" : null}
          onClick={() => setView(VIEW_OPEN_SURVEYS)}>
          {t("sendings_view_open", { count: openSurveys.length })}
        </Chip>
      </div>

      {view === VIEW_SENDINGS && (
        <ListContainer>
          <ListFields>
            <ListCell width={200}>{t("utils_date")}</ListCell>
            <ListCell />
            <ListCell width={150}>{t("account")}</ListCell>
            <ListCell width={200}>{t("project_name")}</ListCell>
            <ListCell width={200}>{t("survey_participation")}</ListCell>
            <ListCell width={150}>{t("email_title")}</ListCell>
            <Space />
            <ListCell width={70}>{t("utils_status")}</ListCell>
          </ListFields>

          {sendings.map((x: any) => (
            <ListItem
              key={x.id}
              isEditable
              className={getSendingClassName(x)}
              onClick={() => props.onClick(new Account(x.Project.Account))}>
              <ListCell width={200}>{formatDate(x.date, true, false)}</ListCell>
              <ListIcon
                fallbackIcon={faShieldAlt}
                iconColor="#1b759a"
                image={
                  x.Project.Account.options === "true"
                    ? getImageUrl("Accounts", x.Project.Account.id)
                    : null
                }
              />

              <ListCell width={150}>{x.Project.Account.name}</ListCell>

              <ListCell width={200}>{x.Project.name}</ListCell>
              <ListCell width={200}>
                {x.Survey ? (
                  getParticipation(x.Survey)
                ) : (
                  <div>{t("utils_unavailable")}</div>
                )}
              </ListCell>

              <ListCell width={150}>{x.Email.name}</ListCell>

              <Space />
              <ListCell width={70}>
                {new Date(x.date).getTime() < Date.now() ? (
                  <FontAwesomeIcon icon={x.isDone ? faCheck : faTimes} className={x.isDone ? "green-t" : "red-t"} />
                ) : (
                  <div>...</div>
                )}
              </ListCell>
            </ListItem>
          ))}
        </ListContainer>
      )}

      {view === VIEW_OPEN_SURVEYS && (
        <ListContainer onSearch={setSearch}>
          <ListItem>
            <Checkbox active={onlyQrcode} onClick={() => setOnlyQrcode(!onlyQrcode)} text={t("sendings_open_qrcode_only")} />
            <div className="width-20" />
            <Checkbox active={onlyOld} onClick={() => setOnlyOld(!onlyOld)} text={t("sendings_open_old_only")} />
            <Space />
            {failedParticipationCount > 0 && (
              <div className="orange-t">
                {t("sendings_open_participation_failed", { count: failedParticipationCount })}{" "}
                <u className="_hover" onClick={retryParticipation}>
                  {t("sendings_open_participation_retry")}
                </u>
              </div>
            )}
            <div className="width-20" />
            <div className="grey-t">{`${displayedOpenSurveys.length} / ${openSurveys.length}`}</div>
          </ListItem>

          <ListFields>
            <ListCell width={200}>{t("sendings_open_launched")}</ListCell>
            <ListCell />
            <ListCell width={150}>{t("account")}</ListCell>
            <ListCell width={200}>{t("project_name")}</ListCell>
            <ListCell width={130}>{t("sendings_open_since")}</ListCell>
            <ListCell width={200}>{t("survey_participation")}</ListCell>
            <ListCell width={250}>{t("sendings_open_diffusion")}</ListCell>
            <Space />
          </ListFields>

          {displayedOpenSurveys.length === 0 && (
            <ListItem>
              <ListCell>{t("utils_no_results")}</ListCell>
            </ListItem>
          )}

          {displayedOpenSurveys.map((x: any) => {
            const participation: any = openParticipation[x.surveyId]
            const diffusionMode: any = x.project.diffusionMode || {}

            return (
              <ListItem key={x.surveyId} isEditable onClick={() => props.onClick(new Account(x.account))}>
                <ListCell width={200}>{formatDate(x.dateStart, true, false)}</ListCell>
                <ListIcon
                  fallbackIcon={faShieldAlt}
                  iconColor="#1b759a"
                  image={x.account.options === "true" ? getImageUrl("Accounts", x.account.id) : null}
                />

                <ListCell width={150}>{x.account.name}</ListCell>

                <ListCell width={200}>{x.project.name}</ListCell>
                <ListCell width={130}>
                  <span className={getOpenDays(x) >= OPEN_SURVEYS_OLD_DAYS ? "orange-t" : ""}>{getOpenSince(x)}</span>
                </ListCell>
                <ListCell width={200}>
                  {participation === undefined ? (
                    <FontAwesomeIcon icon={faCircleNotch} spin color="#8c8c8c" />
                  ) : participation && Number(participation.nb_created) > 0 ? (
                    getParticipation(participation)
                  ) : participation ? (
                    <div>{t("sendings_open_no_invitation")}</div>
                  ) : (
                    <div>{t("utils_unavailable")}</div>
                  )}
                </ListCell>
                <ListCell width={250}>
                  <div className="flex">
                    {diffusionMode.email && <Chip isWithMargin>{t("sendings_mode_email")}</Chip>}
                    {diffusionMode.sms && <Chip isWithMargin>{t("sendings_mode_sms")}</Chip>}
                    {diffusionMode.qrcode && <Chip isWithMargin>{t("sendings_mode_qrcode")}</Chip>}
                  </div>
                </ListCell>
                <Space />
              </ListItem>
            )
          })}
        </ListContainer>
      )}
    </>
  )
}

export default withTranslation()(SendingsAllWidget)
